import { useEffect, useState } from "react";
import { IFormSection, TFormHookParams } from "../../types/common";
import useGet from "../common/useGet";
import {
  API_ACCOUNTS_CREATION_ROLES,
  API_AVAILABLE_LIQUIDATION_BANK_CODES,
  API_MERCHANT_BUSINESS_CONFIGURABLES,
  API_MERCHANT_INDUSTRIES_CONFIGURABLES,
} from "../../utils/apis";
import {
  getRegister,
  getResultOptions,
  itemExists,
  useGetUserPermissions,
} from "../../utils/helpers";
import { URL_APPLICATION_USERS } from "../../utils/urls";
import { useGetOwnerDetails } from "../../utils/owner_details";
import {
  CAN_ADD_LOAN_USER,
  CAN_CHANGE_LOAN_USER,
  CAN_EDIT_USERS,
} from "../../utils/constants";
import {
  ALL_LOCATION_FIELDS,
  TLocationField,
  useLocationDataForm,
} from "../location_data/useLocationDataForm";
import useBrn from "../common/useBrn";
import { VALIDATION_PRESETS } from "../../utils/formUtils";

const MERCHANT_LOCATION_FIELDS: TLocationField[] = [
  "district",
  "county",
  "sub_county",
  "parish",
  "village",
  "physical_address",
];

export const useUsersForm = ({
  id,
  form,
  updateFormValues,
  error,
  setError,
  skipRolesApi = false,
}: TFormHookParams & { skipRolesApi?: boolean } = {}) => {
  const isUpdate = Boolean(id);
  const isMerchant = form?.role_name === "Merchant";
  const isAgent = form?.role_name === "Agent";
  const isBank = form?.preferred_option === "BANK";
  const isTelecom = form?.preferred_option === "TELECOM";

  const { loadData: getRoles, data: roles } = useGet();
  const {
    loadData: getAvailableLiquidationBankCodes,
    data: availableLiquidationBankCodes,
  } = useGet();
  const {
    loadData: getMerchantBusinessConfigurables,
    data: merchantBusinessConfigurables,
  } = useGet();
  const {
    loadData: getMerchantIndustriesConfigurables,
    data: merchantIndustriesConfigurables,
  } = useGet();

  const { brnError, success: brnSuccess } = useBrn(form, updateFormValues);
  const [groups, setGroups] = useState<any>(form?.groups);

  useEffect(() => {
    if (isMerchant) {
      getMerchantBusinessConfigurables({
        api: API_MERCHANT_BUSINESS_CONFIGURABLES,
      });
      getMerchantIndustriesConfigurables({
        api: API_MERCHANT_INDUSTRIES_CONFIGURABLES,
      });
    }
  }, [isMerchant]);

  const ownerDetails = useGetOwnerDetails({
    form,
    updateFormValues,
    error,
    setError,
  });

  const location = useLocationDataForm({
    form,
    include: isMerchant ? MERCHANT_LOCATION_FIELDS : ALL_LOCATION_FIELDS,
    labels: isMerchant ? { sub_county: "Subcounty" } : undefined,
    types: isMerchant ? { physical_address: "textarea" } : undefined,
  });
  const permissions = useGetUserPermissions();

  const filteredRoles = roles?.filter(
    (role: any) => role.name !== "Public User",
  );

  const ownerDetailsDisabled = ownerDetails.map((field) => {
    if (field.props?.disabled === true) {
      return field;
    }

    if (
      [
        "nationality",
        "username",
        "gender",
        "phone_number",
        "nin",
        "email",
      ].includes(field.field_name)
    ) {
      return {
        ...field,
        props: {
          ...field.props,
          disabled: isUpdate,
        },
      };
    }

    return field;
  });

  useEffect(() => {
    if (!skipRolesApi) {
      getRoles({ api: API_ACCOUNTS_CREATION_ROLES });
    }

    setGroups(form?.groups);
  }, [skipRolesApi]);

  useEffect(() => {
    if (setError) {
      setError(brnError);
    }
  }, [brnError]);

  useEffect(() => {
    if (form?.preferred_option === "BANK") {
      getAvailableLiquidationBankCodes({
        api: API_AVAILABLE_LIQUIDATION_BANK_CODES,
      });
    }
  }, [form?.preferred_option]);

  const sections: IFormSection[] = [
    {
      title: "Account Details",
      fields: [
        {
          field_name: "role",
          type: "select",
          label: "Account Type",
          description:
            "The account type is automatically populated when applicable. Otherwise, select the account type.",
          props: {
            required: true,
            disabled: isUpdate,
            value: form?.role?.name,
            placeholder: "Select Account Type",
          },
          options_data: getResultOptions(filteredRoles, "name", "id"),
        },
        {
          field_name: "is_internal_merchant",
          type: "select",
          label: "Is Internal Merchant?",
          visible: isMerchant,
          props: {
            required: isMerchant,
            placeholder: "Select Yes or No",
          },
          options_data: [
            { id: true, name: "Yes" },
            { id: false, name: "No" },
          ],
        },
      ],
    },
    {
      title: "Business Details",
      visible: isMerchant,
      fields: [
        {
          field_name: "brn",
          type: "text",
          label: "BRN",
          description:
            "Enter the Business Registration Number exactly as it appears on the official business registration certificate.",
          props: {
            required: false,
            placeholder: "Enter BRN",
          },
        },
        {
          field_name: "legal_name",
          type: "text",
          label: "Legal Name",
          description:
            "The officially registered name of the business or organization.",
          props: {
            required: false,
            disabled: true,
            placeholder: "Enter Legal Name",
          },
        },
        {
          field_name: "tin",
          type: "text",
          label: "TIN",
          description:
            "Enter the Tax Identification Number issued by the revenue authority.",
          props: {
            required: false,
            placeholder: "Enter TIN",
          },
        },
        {
          field_name: "taxpayer_name",
          type: "text",
          label: "Taxpayer Name",
          description:
            "The taxpayer's official full name as registered under the provided TIN.",
          props: {
            required: false,
            disabled: true,
            placeholder: "Enter Taxpayer Name",
          },
        },
        {
          field_name: "business_name",
          type: "text",
          label: "Business Name",
          description:
            "Enter the official registered business name or commonly used trading name.",
          props: {
            required: true,
            disabled: brnSuccess,
            placeholder: "Enter Business Name",
          },
        },
        {
          field_name: "business_classification_id",
          type: "select",
          label: "Business Classification",
          description:
            "Choose the legal classification of the business, such as company, partnership, or sole proprietor.",
          props: {
            required: true,
            placeholder: "Select Business Classification",
          },
          options_data: merchantBusinessConfigurables?.results?.map(
            (business: any) => ({
              id: business.id,
              name: business.name,
            }),
          ),
        },
        {
          field_name: "industry_id",
          type: "select",
          label: "Industry",
          description:
            "Select the industry that best describes the business operations from the configured list.",
          props: {
            required: true,
            placeholder: "Select Industry",
          },
          options_data: merchantIndustriesConfigurables?.results?.map(
            (business: any) => ({
              id: business.id,
              name: business.name,
            }),
          ),
        },
        {
          field_name: "preferred_option",
          type: "select",
          label: "Preferred Option",
          description:
            "Select the preferred service, payment, or business option.",
          props: {
            required: true,
            placeholder: "Select Preferred Option",
          },
          options_data: [
            { id: "BANK", name: "Bank" },
            { id: "TELECOM", name: "Telecom/Mobile Money" },
          ],
        },
        {
          field_name: "bank_name",
          type: "select",
          label: "Bank Name",
          visible: isBank,
          props: {
            required: isBank,
            placeholder: "Select from the list of banks",
          },
          options_data: availableLiquidationBankCodes?.map((bank: any) => ({
            id: bank.key,
            name: bank.value,
          })),
        },
        {
          field_name: "account_number",
          type: "number",
          label: "Account Number",
          visible: isBank,
          props: {
            required: isBank,
            placeholder: "Enter Account Number",
          },
        },
        {
          field_name: "branch_name",
          type: "text",
          label: "Branch Name",
          visible: isBank,
          props: {
            required: isBank,
            placeholder: "Enter Branch Name",
          },
        },
        {
          field_name: "telecom_mobile_number",
          type: "tel",
          label: "Phone Number (Payment)",
          visible: isTelecom,
          props: {
            required: isTelecom,
            placeholder: "Enter Phone Number",
          },
          validation: VALIDATION_PRESETS.PHONE_UG,
        },
        {
          field_name: "name_on_phone_number",
          type: "text",
          label: "Name on Phone Number",
          visible: isTelecom,
          props: {
            required: isTelecom,
            placeholder: "Enter Name Registered to Phone Number",
          },
        },
      ],
    },
    {
      title: "Contact Person Details",
      visible: isMerchant,
      fields: [
        {
          field_name: "contact_first_name",
          type: "text",
          label: "First Name",
          description: "First name of the authorized contact person.",
          props: {
            required: true,
            placeholder: "Enter First Name",
          },
        },
        {
          field_name: "contact_middle_name",
          type: "text",
          label: "Middle Name",
          description: "Middle name of the authorized contact person.",
          props: {
            required: false,
            placeholder: "Enter Middle Name",
          },
        },
        {
          field_name: "contact_last_name",
          type: "text",
          label: "Last Name",
          description: "Last name of the authorized contact person.",
          props: {
            required: true,
            placeholder: "Enter Last Name",
          },
        },
        {
          field_name: "contact_gender",
          type: "select",
          label: "Gender",
          props: {
            required: true,
            placeholder: "Select Gender",
          },
          options_data: [
            { id: "MALE", name: "Male" },
            { id: "FEMALE", name: "Female" },
            { id: "OTHER", name: "Other" },
          ],
        },
        {
          field_name: "phone_number",
          type: "tel",
          label: "Phone Number",
          description:
            "Enter the contact person's active Uganda or international telephone number.",
          props: {
            required: true,
            placeholder: "Enter Phone Number",
          },
          validation: VALIDATION_PRESETS.PHONE_UG,
        },
        {
          field_name: "email",
          type: "email",
          label: "Email",
          description: "Enter the contact person's email address.",
          props: {
            required: true,
            placeholder: "Enter Email Address",
          },
        },
      ],
    },
    {
      title: "Location Details",
      visible: isMerchant || isAgent,
      fields: [...location.fields(isMerchant, ["physical_address"])],
    },
    {
      title: "Supporting Documents",
      visible: isMerchant || isAgent,
      fields: [
        {
          field_name: "certificate_of_registration",
          type: "file",
          label: "Certificate of Registration",
          description: "Upload the official Certificate of Registration.",
          visible: isMerchant,
          props: {
            required: false,
          },
        },
        {
          field_name: "tin_certificate",
          type: "file",
          label: "TIN Certificate",
          description: "Upload the official TIN Certificate.",
          visible: isMerchant,
          props: {
            required: false,
          },
        },
        {
          field_name: "other",
          type: "file",
          label: "Other Supporting Documents",
          description:
            "Upload additional documents such as a National ID, passport, or trading license.",
          visible: isMerchant || isAgent,
          props: {
            required: false,
            multiple: true,
          },
        },
        {
          field_name: "lc1_letter",
          type: "file",
          label: "LC1 Letter",
          description: "Upload the LC1 letter required for agent registration.",
          visible: isAgent,
          props: {
            required: isAgent,
          },
        },
      ],
    },
    {
      title: "User Details",
      visible: !isMerchant,
      fields: ownerDetailsDisabled,
    },
    {
      title: "System Roles",
      visible:
        itemExists(permissions, [
          CAN_EDIT_USERS,
          CAN_CHANGE_LOAN_USER,
          CAN_ADD_LOAN_USER,
        ]) && form?.is_staff,
      fields: [
        {
          field_name: "groups",
          type: "select",
          label: "System Roles",
          description: "Assign system roles or permission groups to this user.",
          props: {
            required: true,
          },
          options_data: roles,
          value: groups,
        },
      ],
    },
  ];

  const title = "Register User";
  const redirect = getRegister(URL_APPLICATION_USERS);
  const options = {};

  return { sections, title, redirect, options };
};

// import { useEffect, useState } from "react";
// import { IFormSection, TFormHookParams } from "../../types/common";
// import useGet from "../common/useGet";
// import {
//   API_ACCOUNTS_CREATION_ROLES,
//   API_AVAILABLE_LIQUIDATION_BANK_CODES,
// } from "../../utils/apis";
// import {
//   getRegister,
//   getResultOptions,
//   itemExists,
//   useGetUserPermissions,
// } from "../../utils/helpers";
// import { URL_APPLICATION_USERS } from "../../utils/urls";
// import { useGetOwnerDetails } from "../../utils/owner_details";
// import {
//   CAN_ADD_LOAN_USER,
//   CAN_CHANGE_LOAN_USER,
//   CAN_EDIT_USERS,
// } from "../../utils/constants";
// import { useLocationDataForm } from "../location_data/useLocationDataForm";
// import useBrn from "../common/useBrn";
// import { VALIDATION_PRESETS } from "../../utils/formUtils";

// export const useUsersForm = ({
//   id,
//   form,
//   updateFormValues,
//   error,
//   setError,
//   skipRolesApi = false,
// }: TFormHookParams & { skipRolesApi?: boolean } = {}) => {
//   const isUpdate = id ? true : false;
//   const { loadData: getRoles, data: roles } = useGet();
//   const {
//     loadData: getAvailableLiquidationBankCodes,
//     data: availableLiquidationBankCodes,
//   } = useGet();

//   const {
//     // loader: loadBrn,
//     brnError: brnError,
//     success: brnSuccess,
//   } = useBrn(form, updateFormValues);

//   const [groups, setGroups] = useState<any>(form?.groups);

//   const ownerDetails = useGetOwnerDetails({
//     form,
//     updateFormValues,
//     error,
//     setError,
//   });

//   const location = useLocationDataForm();
//   const filteredRoles = roles?.filter(
//     (role: any) => role.name !== "Public User",
//   );

//   const ownerDetailsDisabled = ownerDetails.map((field) => {
//     // If the field is already disabled by the success state, keep it that way
//     if (field.props?.disabled === true) {
//       return field;
//     }
//     // Otherwise, apply the update mode disabled state
//     if (
//       [
//         "nationality",
//         "username",
//         "gender",
//         "phone_number",
//         "nin",
//         "email",
//       ].includes(field.field_name)
//     ) {
//       return { ...field, props: { ...field.props, disabled: isUpdate } };
//     }
//     return field;
//   });

//   const permissions = useGetUserPermissions();

//   useEffect(() => {
//     if (
//       form?.preferred_option === "TELECOM" &&
//       form?.phone_number &&
//       !form?.telecom_mobile_number
//     ) {
//       updateFormValues?.({ telecom_mobile_number: form.phone_number });
//     }
//   }, [
//     form?.phone_number,
//     form?.preferred_option,
//     form?.telecom_mobile_number,
//     updateFormValues,
//   ]);

//   useEffect(() => {
//     if (!skipRolesApi) {
//       getRoles({ api: API_ACCOUNTS_CREATION_ROLES });
//     }
//     setGroups(form?.groups);
//   }, [skipRolesApi]);

//   useEffect(() => {
//     if (setError) {
//       setError(brnError);
//     }
//   }, [brnError]);

//   useEffect(() => {
//     if (form?.preferred_option === "BANK") {
//       getAvailableLiquidationBankCodes({
//         api: API_AVAILABLE_LIQUIDATION_BANK_CODES,
//       });
//     }
//   }, [form?.preferred_option]);

//   const sections: IFormSection[] = [
//     {
//       title: "Account Details",
//       fields: [
//         {
//           field_name: "role",
//           type: "select",
//           label: "Account Type",
//           description:
//             "Select the type of account to assign to this user. This determines their permissions in the system.",
//           props: {
//             required: true,
//             disabled: isUpdate ? true : false,
//             value: form?.role?.name,
//           },
//           options_data: getResultOptions(filteredRoles, "name", "id"),
//         },

//         {
//           field_name: "is_internal_merchant",
//           type: "select",
//           label: "Is Internal Merchant?",
//           description:
//             "Select 'Yes' if the merchant belongs to your internal organization. Select 'No' for external merchants.",
//           visible: ["Merchant"]?.includes(form?.role_name),
//           props: { required: true },
//           options_data: [
//             { id: true, name: "Yes" },
//             { id: false, name: "No" },
//           ],
//         },
//         ...ownerDetailsDisabled,
//       ],
//     },
//     {
//       title: "Business Details",
//       visible: ["Merchant"]?.includes(form?.role_name),
//       fields: [
//         {
//           field_name: "business_name",
//           type: "text",
//           label: "Business Name",
//           description:
//             "Enter the official registered business name or the commonly used trading name.",
//           props: { required: true, disabled: brnSuccess },
//         },
//         {
//           field_name: "business_classification",
//           type: "select",
//           label: "Business Classification",
//           description:
//             "Choose the legal classification of the business such as company, partnership, or sole proprietor.",
//           props: { required: true },
//           options_data: [{ id: "COMPANY", name: "Company" }],
//         },
//         {
//           field_name: "industry",
//           type: "select",
//           label: "Industry",
//           description:
//             "Select the industry that best describes the business operations.",
//           props: { required: true },
//           options_data: [{ id: "BANKING", name: "Banking" }],
//         },
//         {
//           field_name: "preferred_option",
//           type: "select",
//           label: "Preferred Option",
//           description:
//             "Select whether the business prefers to receive settlements through a Telecom or a Bank.",
//           props: { required: true },
//           options_data: [
//             { id: "TELECOM", name: "Telecom" },
//             { id: "BANK", name: "Bank" },
//           ],
//         },

//         {
//           field_name: "telecom_mobile_number",
//           type: "tel",
//           label: "Phone Number",
//           visible: ["TELECOM"]?.includes(form?.preferred_option),
//           description:
//             "Enter the mobile money number that will be used for settlements.",
//           props: { required: true, disabled: true },
//           validation: VALIDATION_PRESETS.PHONE_UG,
//         },

//         {
//           field_name: "bank_name",
//           type: "select",
//           label: "Bank Name",
//           visible: ["BANK"]?.includes(form?.preferred_option),
//           description:
//             "Enter the name of the bank where the settlement account is held.",
//           props: { required: true },
//           options_data: availableLiquidationBankCodes?.map((bank: any) => ({
//             id: bank.key,
//             name: bank.value,
//           })),
//         },
//         {
//           field_name: "account_number",
//           type: "number",
//           label: "Account Number",
//           visible: ["BANK"]?.includes(form?.preferred_option),
//           description: "Enter the bank account number for settlement payouts.",
//           props: { required: true },
//         },
//         {
//           field_name: "branch_name",
//           type: "text",
//           label: "Branch Name",
//           visible: ["BANK"]?.includes(form?.preferred_option),
//           description:
//             "Enter the branch name of the bank where the account is registered.",
//           props: { required: true },
//         },
//       ],
//     },

//     {
//       title: "Location Details",
//       fields: [...location?.fields(false)],
//       visible: ["Merchant", "Agent"].includes(form?.role_name),
//     },
//     // ROLES

//     {
//       title: "Account Details",
//       visible:
//         itemExists(permissions, [
//           CAN_EDIT_USERS,
//           CAN_CHANGE_LOAN_USER,
//           CAN_ADD_LOAN_USER,
//         ]) && form?.is_staff,

//       fields: [
//         {
//           field_name: "groups",
//           type: "select",
//           label: "System Roles",
//           description:
//             "Assign system roles or permissions groups to this user. These control what they can access.",
//           props: { required: true },
//           options_data: roles,
//           value: groups,
//         },
//       ],
//     },

//     {
//       title: "Supporting Documents",
//       visible: ["Merchant", "Agent"]?.includes(form?.role_name),
//       fields: [
//         {
//           field_name: "certificate_of_registration",
//           type: "file",
//           label: "Certificate of Registration",
//           description:
//             "Upload the official certificate showing the business is legally registered.",
//           visible: ["Merchant"]?.includes(form?.role_name),
//           props: { required: true },
//         },
//         {
//           field_name: "tin_certificate",
//           type: "file",
//           label: "TIN Certificate",
//           description:
//             "Upload the Tax Identification Number certificate issued by the revenue authority.",
//           visible: ["Merchant"]?.includes(form?.role_name),
//           props: { required: true },
//         },
//         {
//           field_name: "lc1_letter",
//           type: "file",
//           label: "LC1 Letter",
//           description:
//             "Upload a signed and stamped LC1 introduction letter confirming the agent’s residence or business location.",
//           visible: ["Agent"]?.includes(form?.role_name),
//           props: { required: true },
//         },
//         {
//           field_name: "other",
//           type: "file",
//           label: "Other Supporting Documents",
//           description:
//             "Upload any additional documents required for verification (optional).",
//           visible: ["Merchant", "Agent"]?.includes(form?.role_name),
//           props: { required: false },
//         },
//       ],
//     },
//   ];

//   const title = "Register User";
//   const redirect = getRegister(URL_APPLICATION_USERS);

//   // const getConfigOptions = () => {
//   // const { loadData: getRoles, data: roles } = useGet();
//   // useGet();
//   // useEffect(() => {
//   //   getRoles({
//   //     api: API_ACCOUNTS_ROLES,
//   //     params: {},
//   //   });
//   // }, []);
//   // return {
//   //   roles: getResultOptions(roles, "name", "id"),
//   // };
//   // };

//   const options = {
//     // ...getConfigOptions(),
//   };
//   return { sections, title, redirect, options };
// };
